import JSZip from "jszip";
import type { BookLanguage } from "@japa-reader/contracts";
import { detectBookLanguage, normalizeBookLanguage } from "./book-language";

export type ExtractedBook = {
  title: string;
  author: string;
  language: BookLanguage;
  text: string;
  chapters: ExtractedChapter[];
};

export type ExtractedChapter = {
  title: string;
  text: string;
};

const BLOCK_SELECTOR = "h1,h2,h3,h4,h5,h6,p,li,blockquote,figcaption";

export async function extractBook(file: File): Promise<ExtractedBook> {
  const extension = file.name.split(".").pop()?.toLowerCase();

  if (extension === "txt" || file.type.startsWith("text/")) {
    const text = normalizeText(await file.text());
    return {
      title: titleFromFilename(file.name),
      author: "",
      language: detectBookLanguage(text),
      text,
      chapters: [{ title: chapterTitleFromText(text, 0), text }],
    };
  }

  if (extension !== "epub") {
    throw new Error("Use um arquivo EPUB ou TXT para o texto do capítulo.");
  }

  const zip = await JSZip.loadAsync(file);
  const containerXml = await readZipText(zip, "META-INF/container.xml");
  const container = parseXml(containerXml);
  const rootfile = container.querySelector("rootfile")?.getAttribute("full-path");
  if (!rootfile) throw new Error("Não encontrei o conteúdo principal deste EPUB.");

  const opf = parseXml(await readZipText(zip, rootfile));
  const titleNode = Array.from(opf.getElementsByTagName("*")).find((node) => node.localName === "title");
  const authorNode = Array.from(opf.getElementsByTagName("*")).find((node) => node.localName === "creator");
  const languageNode = Array.from(opf.getElementsByTagName("*")).find((node) => node.localName === "language");
  const title = normalizeText(titleNode?.textContent ?? "") || titleFromFilename(file.name);
  const author = normalizeText(authorNode?.textContent ?? "");
  const manifest = new Map<string, string>();

  opf.querySelectorAll("manifest > item").forEach((item) => {
    const id = item.getAttribute("id");
    const href = item.getAttribute("href");
    if (id && href) manifest.set(id, href);
  });

  const chapterPaths = Array.from(opf.querySelectorAll("spine > itemref"))
    .map((item) => item.getAttribute("idref"))
    .filter((id): id is string => Boolean(id))
    .map((id) => manifest.get(id))
    .filter((href): href is string => Boolean(href))
    .map((href) => resolveZipPath(rootfile, href));

  const chapters: ExtractedChapter[] = [];
  for (const [index, path] of chapterPaths.entries()) {
    const entry = zip.file(path);
    if (!entry) continue;
    const chapter = extractXhtmlChapter(await entry.async("text"), index);
    if (chapter.text) chapters.push(chapter);
  }

  const text = normalizeText(chapters.map((chapter) => chapter.text).join("\n\n"));
  if (!text) throw new Error("O EPUB não contém texto legível para alinhar.");
  const metadataLanguage = normalizeMetadataLanguage(languageNode?.textContent);
  return { title, author, language: metadataLanguage ?? detectBookLanguage(text), text, chapters };
}

function normalizeMetadataLanguage(value: string | null | undefined): BookLanguage | null {
  if (!value?.trim()) return null;
  const normalized = value.trim().toLowerCase();
  if (!/^(ja|jpn|en|eng|ru|rus)(?:[-_].*)?$/u.test(normalized)) return null;
  return normalizeBookLanguage(normalized);
}

function extractXhtmlChapter(source: string, index: number): ExtractedChapter {
  let doc = new DOMParser().parseFromString(source, "application/xhtml+xml");
  if (doc.querySelector("parsererror")) {
    doc = new DOMParser().parseFromString(source, "text/html");
  }

  doc.querySelectorAll("script,style,nav,svg,rt,rp,noscript").forEach((node) => node.remove());
  const blocks = Array.from(doc.querySelectorAll(BLOCK_SELECTOR));
  const leafBlocks = blocks.filter((block) => !block.querySelector(BLOCK_SELECTOR));
  const sourceBlocks = leafBlocks.length ? leafBlocks : [doc.body ?? doc.documentElement];
  const text = normalizeText(sourceBlocks.map((node) => node.textContent ?? "").join("\n"));
  const heading = normalizeText(doc.querySelector("h1,h2,h3,title")?.textContent ?? "");
  return {
    title: heading && heading.length <= 80 ? heading : chapterTitleFromText(text, index),
    text,
  };
}

function parseXml(source: string): Document {
  const doc = new DOMParser().parseFromString(source, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("O EPUB contém um arquivo interno inválido.");
  return doc;
}

async function readZipText(zip: JSZip, path: string): Promise<string> {
  const entry = zip.file(path);
  if (!entry) throw new Error(`Arquivo interno ausente no EPUB: ${path}`);
  return entry.async("text");
}

function resolveZipPath(baseFile: string, relative: string): string {
  const cleanRelative = relative.split(/[?#]/)[0];
  const parts = `${baseFile.slice(0, baseFile.lastIndexOf("/") + 1)}${cleanRelative}`.split("/");
  const resolved: string[] = [];
  for (const part of parts) {
    if (!part || part === ".") continue;
    if (part === "..") resolved.pop();
    else resolved.push(decodeURIComponent(part));
  }
  return resolved.join("/");
}

export function normalizeText(value: string): string {
  return value
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[\t ]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function titleFromFilename(filename: string): string {
  return filename.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim() || "Meu livro";
}

function chapterTitleFromText(text: string, index: number): string {
  const firstLine = text.split(/\n+/).map((line) => line.trim()).find(Boolean) ?? "";
  return firstLine && firstLine.length <= 80 ? firstLine : `Capítulo ${index + 1}`;
}
